import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE = 'cart-progress-discount';
const SYNC_DELAY_MS = 200;

/**
 * @returns {{ code: string; minItems: number } | null}
 */
function getProgressDiscountConfig() {
  const config = window.Theme?.cartProgressDiscount;
  const code = config?.code?.trim() ?? '';
  const minItems = Number(config?.minItems) || 2;

  if (code) return { code, minItems };

  const fromDom = document.querySelector('.cart-progress-discount-data')?.dataset?.discountCode?.trim();
  if (!fromDom) return null;

  const minFromDom = Number(document.querySelector('.cart-progress-discount-data')?.dataset?.minItems) || 2;
  return { code: fromDom, minItems: minFromDom };
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
    if (node instanceof HTMLElement && node.dataset.sectionId) {
      ids.add(node.dataset.sectionId);
    }
  });

  const fromDom = document.querySelector('.cart-progress-discount-data')?.dataset?.sectionId;
  if (fromDom) ids.add(fromDom);

  return [...ids];
}

/**
 * @param {{ items: Array<{ quantity: number }> }} cart
 * @param {number} minItems
 */
function isCartEligible(cart, minItems) {
  const itemCount = cart.items?.reduce((total, item) => total + item.quantity, 0) ?? 0;
  return itemCount >= minItems;
}

/**
 * @param {{ discount_codes?: Array<{ code: string; applicable: boolean }>; total_discount?: number; items?: Array<{ line_level_discount_allocations?: unknown[] }> }} cart
 * @param {string} code
 */
function isProgressDiscountApplied(cart, code) {
  const normalizedCode = code.toUpperCase();
  const codeEntry = cart.discount_codes?.find(
    (/** @type {{ code: string; applicable: boolean }} */ entry) => entry.code.toUpperCase() === normalizedCode
  );

  if (codeEntry?.applicable !== true) return false;

  if ((cart.total_discount ?? 0) > 0) return true;

  return (
    cart.items?.some((item) => (item.line_level_discount_allocations?.length ?? 0) > 0) ?? false
  );
}

/**
 * @param {string} discount
 * @param {string[]} sectionIds
 */
async function updateCartDiscount(discount, sectionIds) {
  const body = JSON.stringify({
    discount,
    ...(sectionIds.length ? { sections: sectionIds } : {}),
  });

  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));

  if (!response.ok) {
    throw new Error(`Cart discount update failed (${response.status})`);
  }

  return response.json();
}

let isSyncing = false;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let syncTimer;

/**
 * @param {string} source
 */
export async function syncProgressDiscount(source = SOURCE) {
  if (isSyncing) return;

  const config = getProgressDiscountConfig();
  if (!config) return;

  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();

  const eligible = isCartEligible(cart, config.minItems);
  const sectionIds = getCartSectionIds();
  const normalizedCode = config.code.toUpperCase();

  const existingCodes =
    cart.discount_codes?.map((/** @type {{ code: string }} */ entry) => entry.code).filter(Boolean) ?? [];

  const hasCode = existingCodes.some((code) => code.toUpperCase() === normalizedCode);
  const discountApplied = isProgressDiscountApplied(cart, config.code);

  if (eligible && discountApplied) return;
  if (!eligible && !hasCode) return;

  const nextCodes = eligible
    ? [...existingCodes.filter((code) => code.toUpperCase() !== normalizedCode), config.code]
    : existingCodes.filter((code) => code.toUpperCase() !== normalizedCode);

  isSyncing = true;

  try {
    const data = await updateCartDiscount(nextCodes.join(','), sectionIds);
    const updatedCart = data.items ? data : await fetch('/cart.js').then((response) => response.json());

    let morphed = false;

    for (const sectionId of sectionIds) {
      const sectionHtml = data.sections?.[sectionId];
      if (sectionHtml) {
        morphSection(sectionId, sectionHtml);
        morphed = true;
      }
    }

    if (morphed) {
      document.dispatchEvent(
        new CartUpdateEvent({}, sectionIds[0] ?? '', {
          source: SOURCE,
          sections: data.sections,
        })
      );
    } else if (sectionIds.length > 0) {
      document.dispatchEvent(
        new CartUpdateEvent({}, sectionIds[0] ?? '', {
          source: SOURCE,
        })
      );
    }

    if (eligible && !isProgressDiscountApplied(updatedCart, config.code) && source !== 'retry') {
      window.setTimeout(() => syncProgressDiscount('retry'), 500);
    }
  } catch (error) {
    console.error('[cart-progress-discount]', error);
  } finally {
    isSyncing = false;
  }
}

/**
 * @param {string} [source]
 */
function scheduleSync(source = SOURCE) {
  if (syncTimer) window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    syncProgressDiscount(source);
  }, SYNC_DELAY_MS);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event instanceof CartUpdateEvent && event.detail?.data?.source === SOURCE) return;
  scheduleSync();
});

document.addEventListener('cart-drawer:open', () => {
  scheduleSync('drawer-open');
});

document.addEventListener(
  'click',
  async (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutTrigger = event.target.closest('#checkout, [name="checkout"]');
    if (!checkoutTrigger || checkoutTrigger.dataset.progressDiscountSyncing === 'true') return;

    const config = getProgressDiscountConfig();
    if (!config) return;

    const cart = await fetch('/cart.js').then((response) => response.json());
    const eligible = isCartEligible(cart, config.minItems);
    if (!eligible) return;

    if (isProgressDiscountApplied(cart, config.code)) return;

    event.preventDefault();

    checkoutTrigger.dataset.progressDiscountSyncing = 'true';

    try {
      await syncProgressDiscount('checkout');
    } finally {
      delete checkoutTrigger.dataset.progressDiscountSyncing;

      const formId = checkoutTrigger.getAttribute('form');
      const form =
        (formId ? document.getElementById(formId) : null) ??
        (checkoutTrigger instanceof HTMLButtonElement ? checkoutTrigger.form : null) ??
        checkoutTrigger.closest('form');

      if (form instanceof HTMLFormElement) {
        form.submit();
      }
    }
  },
  { capture: true }
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleSync('load'));
} else {
  scheduleSync('load');
}
